import { useMemo, useState } from 'react';
import { Info, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useCreateInvite } from '@/hooks/use-committee';

interface InviteEvaluatorDialogProps {
  open: boolean;
  eventId: string;
  onOpenChange: (open: boolean) => void;
}

export function InviteEvaluatorDialog({ open, eventId, onOpenChange }: InviteEvaluatorDialogProps) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [inlineError, setInlineError] = useState('');

  const createInvite = useCreateInvite(eventId);

  const isEmailValid = useMemo(() => /^\S+@\S+\.\S+$/.test(email.trim()), [email]);
  const isMessageValid = useMemo(() => message.length <= 500, [message]);
  const canSubmit = isEmailValid && isMessageValid && Boolean(eventId) && !createInvite.isPending;

  const resetForm = () => {
    setEmail('');
    setMessage('');
    setInlineError('');
  };

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetForm();
    }
  };

  const handleSubmit = async () => {
    setInlineError('');

    if (!isEmailValid || !isMessageValid) {
      setInlineError('No fue posible enviar la invitación. Verifica el correo o intenta más tarde.');
      return;
    }

    try {
      await createInvite.mutateAsync({
        email: email.trim().toLowerCase(),
        message: message.trim() || undefined,
      });
      toast.success('Invitación enviada correctamente.');
      handleClose(false);
    } catch {
      setInlineError('No fue posible enviar la invitación. Verifica el correo o intenta más tarde.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-[#1e3a5f]">
            <span>Invitar Evaluador</span>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-evaluator-email">Correo electrónico del evaluador</Label>
            <Input
              id="invite-evaluator-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (inlineError) setInlineError('');
              }}
              placeholder="ejemplo@unach.mx"
              className={inlineError ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {inlineError ? (
              <Alert className="border-destructive bg-red-50 text-destructive">
                <AlertDescription>{inlineError}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <Alert className="border-blue-200 bg-blue-50 text-blue-900">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              Los correos institucionales usarán autenticación LDAP automáticamente (según dominios configurados).
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="invite-evaluator-message">Mensaje opcional</Label>
            <Textarea
              id="invite-evaluator-message"
              value={message}
              maxLength={500}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Escriba un mensaje personalizado para el evaluador..."
              className="min-h-[120px]"
            />
            <p className="text-sm text-gray-500">Se enviará un enlace de invitación al correo proporcionado.</p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={createInvite.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="bg-[#c9a227] text-white hover:bg-[#b8921f]"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {createInvite.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Enviar Invitación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
